'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface SortOption<T> {
  label: string
  value: string
  compareFn: (a: T, b: T) => number
}

export interface UseContentFilterOptions<T> {
  items: T[]
  searchFields: (keyof T)[]
  sortOptions: SortOption<T>[]
  defaultSort?: string
  categoryAccessor: (item: T) => string | null | undefined
}

export interface ContentFilterState {
  search: string
  sort: string
  category: string
  isExpanded: boolean
}

export function useContentFilter<T>({
  items,
  searchFields,
  sortOptions,
  defaultSort = 'newest',
  categoryAccessor,
}: UseContentFilterOptions<T>) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [isExpanded, setIsExpanded] = useState(false)
  const [search, setSearchRaw] = useState(searchParams.get('search') || '')
  const [sort, setSortRaw] = useState(searchParams.get('sortBy') || defaultSort)
  const [category, setCategoryRaw] = useState(searchParams.get('category') || 'all')

  // Re-sync state from URL params on browser back/forward
  useEffect(() => {
    const urlSearch = searchParams.get('search') || ''
    const urlSort = searchParams.get('sortBy') || defaultSort
    const urlCategory = searchParams.get('category') || 'all'
    if (urlSearch !== search) setSearchRaw(urlSearch)
    if (urlSort !== sort) setSortRaw(urlSort)
    if (urlCategory !== category) setCategoryRaw(urlCategory)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when URL changes
  }, [searchParams])

  // Escape key closes the drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isExpanded])

  const updateUrl = useCallback(
    (params: Record<string, string>) => {
      const url = new URLSearchParams()
      const merged = { search, sortBy: sort, category, ...params }
      for (const [k, v] of Object.entries(merged)) {
        if (v && v !== 'all' && v !== defaultSort && v !== '') {
          url.set(k, v)
        }
      }
      const qs = url.toString()
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    },
    [router, pathname, search, sort, category, defaultSort],
  )

  const setSearch = useCallback(
    (value: string) => {
      setSearchRaw(value)
      updateUrl({ search: value })
    },
    [updateUrl],
  )

  const setSort = useCallback(
    (value: string) => {
      setSortRaw(value)
      updateUrl({ sortBy: value })
    },
    [updateUrl],
  )

  const setCategory = useCallback(
    (value: string) => {
      setCategoryRaw(value)
      updateUrl({ category: value })
    },
    [updateUrl],
  )

  const clearFilters = useCallback(() => {
    setSearchRaw('')
    setSortRaw(defaultSort)
    setCategoryRaw('all')
    router.replace(pathname, { scroll: false })
  }, [router, pathname, defaultSort])

  const hasActiveFilters = search !== '' || category !== 'all' || sort !== defaultSort

  const activeSortOption = sortOptions.find((o) => o.value === sort)

  const filtered = useMemo(() => {
    let result = items

    // Category filter
    if (category !== 'all') {
      result = result.filter((item) => {
        const cat = categoryAccessor(item)
        return cat?.toLowerCase() === category.toLowerCase()
      })
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter((item) =>
        searchFields.some((field) => {
          const val = item[field]
          return typeof val === 'string' && val.toLowerCase().includes(q)
        }),
      )
    }

    // Sort
    if (activeSortOption) {
      result = [...result].sort(activeSortOption.compareFn)
    }

    return result
  }, [items, category, search, searchFields, activeSortOption, categoryAccessor])

  return {
    // State
    search,
    sort,
    category,
    isExpanded,
    hasActiveFilters,
    activeSortLabel: activeSortOption?.label || null,
    // Filtered output
    filtered,
    totalCount: items.length,
    filteredCount: filtered.length,
    // Actions
    setSearch,
    setSort,
    setCategory,
    setIsExpanded,
    clearFilters,
  }
}
